import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { FileText, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SuperAdminPeek } from "@/components/SuperAdminPeek";
import { Meta } from "@/components/auction/shared";
import { CreatorView } from "@/components/auction/CreatorView";
import { BidderView } from "@/components/auction/BidderView";
import { BopBidderView } from "@/components/auction/BopBidderView";
import { FreightBidderView } from "@/components/auction/FreightBidderView";
import { WinnerView } from "@/components/auction/WinnerView";
import { RoundsHistory } from "@/components/auction/RoundsHistory";

export default function AuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setA(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      if (e.response?.status === 404 || e.response?.status === 403) navigate("/");
      setA(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (a === null) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="p-10 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading auction...</div>
      </div>
    );
  }
  if (!a) {
    return (
      <div className="min-h-screen bg-white" data-testid="auction-load-error">
        <TopBar />
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-16">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Error</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tightest mb-4">Couldn&apos;t load this auction</h1>
          <p className="text-sm text-neutral-600 mb-6 leading-relaxed">
            The server returned an unexpected response. This is usually a transient backend issue — please try refreshing in a few seconds.
            If the issue persists, the deployment may need to be redeployed.
          </p>
          <div className="flex gap-3">
            <button onClick={refresh} className="rounded-none bg-[#0B1929] text-white h-10 px-4 text-xs tracking-[0.18em] uppercase font-semibold btn-sharp" data-testid="retry-load">
              Try again
            </button>
            <button onClick={() => navigate("/")} className="rounded-none border border-black/30 h-10 px-4 text-xs tracking-[0.18em] uppercase font-semibold btn-sharp" data-testid="back-home">
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="auction-detail-page">
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <Header a={a} />
        {a.you_are_disqualified && (
          <DisqualifiedBanner reason={a.disqualification_reason} at={a.disqualified_at} />
        )}
        {a.you_are_disqualified ? (
          <div className="border border-black/10 p-8 text-sm text-neutral-600 leading-relaxed" data-testid="disqualified-locked-view">
            This auction is closed for you. You cannot submit or update bids.
            If you believe this was done in error, please contact the auction
            creator directly ({a.creator_name || "—"}).
          </div>
        ) : a.status === "closed" ? (
          <WinnerView a={a} isCreator={a.is_creator || a.is_super_admin_view} />
        ) : a.is_creator || a.is_super_admin_view ? (
          <CreatorView a={a} onChange={refresh} />
        ) : a.type === "bop" ? (
          <BopBidderView a={a} onChange={refresh} />
        ) : a.type === "freight" ? (
          <FreightBidderView a={a} onChange={refresh} />
        ) : (
          <BidderView a={a} onChange={refresh} />
        )}
        <RoundsHistory a={a} />
      </div>
    </div>
  );
}

const Header = ({ a }) => {
  const { user } = useAuth();
  const showPeek = user?.is_super_admin;
  return (
    <div className="border-b border-black pb-6 mb-8" data-testid="auction-header">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-[10px] tracking-[0.3em] uppercase text-neutral-500" data-testid="auction-view-label">
          {a.is_creator ? "Your Auction" : a.is_super_admin_view ? "Super Admin View" : "Bidder View"}
        </span>
        {a.type === "bop" && (
          <span className="text-[10px] tracking-[0.25em] uppercase font-semibold border border-[#22D3EE] text-[#22D3EE] px-2 py-1" data-testid="auction-type-badge">
            BOP RFQ
          </span>
        )}
        {a.type === "freight" && (
          <span className="text-[10px] tracking-[0.25em] uppercase font-semibold border border-[#22D3EE] text-[#22D3EE] px-2 py-1" data-testid="auction-type-badge">
            Freight
          </span>
        )}
        {a.you_are_disqualified ? (
          <span
            className="text-[10px] tracking-[0.25em] uppercase font-semibold border border-[#B91C1C] text-[#B91C1C] px-2 py-1 inline-flex items-center gap-1"
            data-testid="status-pill-disqualified"
          >
            <Prohibit size={11} weight="bold" /> Disqualified
          </span>
        ) : (
          <span className="text-[10px] tracking-[0.25em] uppercase font-semibold border border-black/30 px-2 py-1">
            {a.status === "closed" ? "Closed" : `Open · Round ${a.current_round}`}
          </span>
        )}
        {showPeek && (
          <div className="ml-auto">
            <SuperAdminPeek auctionId={a.id} />
          </div>
        )}
      </div>
      <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tightest leading-none" data-testid="auction-title">{a.title}</h1>
      <p className="text-sm text-neutral-600 mt-3 max-w-3xl">{a.description}</p>
      <div className="flex flex-wrap gap-6 mt-5 text-xs">
        {a.deadline && (
          <Meta
            label={
              (a.deadline_history?.length ?? 0) > 0
                ? `Deadline · extended ${a.deadline_history.length}×`
                : "Deadline"
            }
            value={new Date(a.deadline).toLocaleString()}
          />
        )}
        <Meta label="Created by" value={a.creator_name || "—"} />
        {(a.is_creator || a.is_super_admin_view) && (
          <Meta label="Invited" value={`${a.invited_emails?.length ?? a.invited_count ?? 0} bidders`} />
        )}        {a.requirement_path && (
          <a
            href={`${API}/files/download?path=${encodeURIComponent(a.requirement_path)}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs uppercase tracking-[0.25em] font-semibold underline underline-offset-4 flex items-center gap-1.5"
            data-testid="download-requirement"
          >
            <FileText size={14} weight="bold" />
            {a.requirement_filename || "Requirement PDF"}
          </a>
        )}
      </div>
    </div>
  );
};


const DisqualifiedBanner = ({ reason, at }) => {
  let when = "";
  if (at) {
    try { when = new Date(at).toLocaleString(); } catch { when = at; }
  }
  return (
    <div
      className="border border-[#B91C1C] bg-red-50 p-5 mb-6 flex items-start gap-3"
      data-testid="disqualified-banner"
    >
      <Prohibit size={22} weight="fill" className="text-[#B91C1C] shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#B91C1C] mb-1">
          You have been disqualified from this auction
        </div>
        <div className="text-sm text-[#7F1D1D] leading-relaxed" data-testid="disqualified-banner-reason">
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">Reason from creator:</span>{" "}
          {reason ? <span className="italic">&ldquo;{reason}&rdquo;</span> : <span className="italic text-neutral-500">No reason provided.</span>}
        </div>
        {when && (
          <div className="text-[11px] font-mono-plex text-neutral-500 mt-1.5">
            Recorded {when}
          </div>
        )}
      </div>
    </div>
  );
};
